import {
  DataMessageTypeEnum,
  TerraUIMessageDataPartName,
  type DataMessageType,
  type TerraUIMessage,
  type ToolCallGenericMessage,
} from '@terra/shared-dto-chat-types';
import { isObject } from 'radash';
import { readObjectValue } from '../../utils/readObjectValue';

const ASSISTANT_ROLE = 'assistant';
const USER_ROLE = 'user';
const OBJECT_CREATOR_TOOL = 'object-creator';
const FINDING_DRAFT_TOOLS = new Set([
  'propose-finding-update',
  'rerun-finding-cvss',
]);

const TERMINAL_TOOL_CALL_TYPES = new Set<DataMessageTypeEnum>([
  DataMessageTypeEnum.ToolCallComplete,
  DataMessageTypeEnum.ToolCallError,
]);

const TOOL_CALL_DATA_TYPES = [
  DataMessageTypeEnum.ToolCallStart,
  DataMessageTypeEnum.ToolCallUpdate,
  DataMessageTypeEnum.ToolCallComplete,
  DataMessageTypeEnum.ToolCallError,
] as const satisfies readonly DataMessageTypeEnum[];

export function resolveCopilotFindingEditProcessingId({
  messages,
  linkedFindingId,
}: {
  messages: TerraUIMessage[];
  linkedFindingId?: string;
}): string | null {
  const toolCalls = readRecentAssistantToolCalls(messages);
  const activeProposeFindingId = readActiveProposeFindingId(toolCalls);
  const findingId = linkedFindingId ?? activeProposeFindingId;

  if (!findingId) {
    return null;
  }

  if (activeProposeFindingId === findingId) {
    return findingId;
  }

  if (
    linkedFindingId === findingId &&
    hasActiveObjectCreatorTool(toolCalls) &&
    hasProposeFindingActivityInTurn({ toolCalls, findingId })
  ) {
    return findingId;
  }

  return null;
}

function hasProposeFindingActivityInTurn({
  toolCalls,
  findingId,
}: {
  toolCalls: ToolCallGenericMessage[];
  findingId: string;
}): boolean {
  return toolCalls.some(
    (toolCall) =>
      FINDING_DRAFT_TOOLS.has(normalizeToolName(toolCall.toolName)) &&
      readProposeFindingId(toolCall) === findingId,
  );
}

function hasActiveObjectCreatorTool(
  toolCalls: ToolCallGenericMessage[],
): boolean {
  const latestByToolCallId = toolCalls.reduce((latest, toolCall) => {
    if (normalizeToolName(toolCall.toolName) !== OBJECT_CREATOR_TOOL) {
      return latest;
    }

    latest.set(toolCall.toolCallId, toolCall);

    return latest;
  }, new Map<string, ToolCallGenericMessage>());

  return [...latestByToolCallId.values()].some(
    (toolCall) => !TERMINAL_TOOL_CALL_TYPES.has(toolCall.type),
  );
}

export function readCopilotFindingEditFieldCount({
  messages,
  findingId,
}: {
  messages: TerraUIMessage[];
  findingId: string;
}): number {
  const toolCalls = readRecentAssistantToolCalls(messages);
  const matchingPropose = toolCalls.findLast(
    (toolCall) =>
      FINDING_DRAFT_TOOLS.has(normalizeToolName(toolCall.toolName)) &&
      readProposeFindingId(toolCall) === findingId,
  );

  return matchingPropose ? countProposePatchFields(matchingPropose) : 0;
}

function readActiveProposeFindingId(
  toolCalls: ToolCallGenericMessage[],
): string | null {
  const activePropose = toolCalls.findLast(
    (toolCall) =>
      FINDING_DRAFT_TOOLS.has(normalizeToolName(toolCall.toolName)) &&
      !TERMINAL_TOOL_CALL_TYPES.has(toolCall.type),
  );

  return activePropose ? readProposeFindingId(activePropose) : null;
}

function readRecentAssistantToolCalls(
  messages: TerraUIMessage[],
): ToolCallGenericMessage[] {
  const lastUserMessageIndex = messages.findLastIndex(
    (message) => message.role === USER_ROLE,
  );

  return messages
    .slice(lastUserMessageIndex + 1)
    .filter((message) => message.role === ASSISTANT_ROLE)
    .flatMap((message) =>
      message.parts.flatMap((part) => {
        const toolCall = readToolCallDataPart(part);

        return toolCall ? [toolCall] : [];
      }),
    );
}

function readToolCallDataPart(
  part: TerraUIMessage['parts'][number],
): ToolCallGenericMessage | null {
  if (
    part.type !== TerraUIMessageDataPartName ||
    !part.data ||
    !isToolCallDataMessage(part.data)
  ) {
    return null;
  }

  return part.data;
}

function isToolCallDataMessage(
  data: DataMessageType,
): data is ToolCallGenericMessage {
  return (TOOL_CALL_DATA_TYPES as readonly DataMessageTypeEnum[]).includes(
    data.type,
  );
}

function readProposeFindingId(toolCall: ToolCallGenericMessage): string | null {
  const input = isObject(toolCall.input)
    ? (toolCall.input as Record<string, unknown>)
    : null;
  const output = isObject(toolCall.output)
    ? (toolCall.output as Record<string, unknown>)
    : null;

  return (
    (input
      ? (readOptionalString(readObjectValue(input, 'findingId')) ??
        readOptionalString(readObjectValue(input, 'objectId')))
      : undefined) ??
    (output
      ? readOptionalString(readObjectValue(output, 'objectId'))
      : undefined) ??
    null
  );
}

function countProposePatchFields(toolCall: ToolCallGenericMessage): number {
  const input = isObject(toolCall.input)
    ? (toolCall.input as Record<string, unknown>)
    : null;
  const output = isObject(toolCall.output)
    ? (toolCall.output as Record<string, unknown>)
    : null;
  const patchCandidate =
    (input ? readObjectValue(input, 'patch') : undefined) ??
    (output ? readObjectValue(output, 'patch') : undefined);
  const patch = isObject(patchCandidate)
    ? (patchCandidate as Record<string, unknown>)
    : null;

  if (!patch) {
    return 0;
  }

  return Object.values(patch).filter((value) => value !== undefined).length;
}

function readOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0
    ? value
    : undefined;
}

function normalizeToolName(value: string | undefined): string {
  return (value ?? '').replace(/_/g, '-').toLowerCase();
}
